// Client-side comparators for the assessment candidate table (StudentsTable).
//
// getAssessmentDetails returns EVERY candidate of an assessment (pagination is
// opt-in via `paginate`, which this screen does not pass) and StudentsTable pages
// the list in the browser. Sorting therefore has to run over the whole list
// BEFORE the page slice — if antd did the sorting it would only reorder the ten
// rows currently on screen. So sortable columns keep `sorter: true` (antd draws
// the affordance and reports clicks, but does not reorder) and carry their own
// `sortValue` / `sortKind`, which sortRows() applies to the full list.
//
// Values need normalising before they can be compared: admin-node sends dates
// pre-formatted ("03 Jul 2026", "30 Jul 2026, 5:42 PM"), and score fields hold
// the status string ("Pending") until scores_calculated flips true. Anything that
// cannot be normalised counts as blank and sorts last in BOTH directions, so an
// unscored candidate never outranks a scored one.
//
// See studentSorters.test.js.

const moment = require('moment')

const SORT_KIND = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
}

// Most specific first — moment scores every candidate format and keeps the best
// match, so a date-only value still parses against the date+time pattern.
const DATE_FORMATS = ['DD MMM YYYY, h:mm A', 'DD MMM YYYY', moment.ISO_8601]

// Behaviour proficiency ladder, weakest first. Levels outside the ladder sort
// after every known level (and tie-break by name) rather than being dropped.
const PROFICIENCY_ORDER = ['beginner', 'apprentice', 'practitioner', 'expert']

// Proctoring outcomes, best first. Shared with the column renderer so the label
// shown and the value sorted can never disagree.
const PROCTORING_ORDER = ['Good', 'Bad', 'Evaluating']

const isBlank = value =>
  value === null || value === undefined || value === '' || value === '-'

// Score fields carry the status string ("Pending", "In Progress") while
// scores_calculated is false, so only real finite numbers count.
const toNumber = value =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const toTimestamp = value => {
  if (isBlank(value)) {
    return null
  }
  const parsed = moment(value, DATE_FORMATS)
  return parsed.isValid() ? parsed.valueOf() : null
}

const toText = value => (isBlank(value) ? null : String(value).trim().toLowerCase() || null)

// Normalise a raw cell value into something comparable, or null when it is
// missing/unusable for this kind of sort.
const toComparable = (kind, value) => {
  if (kind === SORT_KIND.NUMBER) {
    return toNumber(value)
  }
  if (kind === SORT_KIND.DATE) {
    return toTimestamp(value)
  }
  return toText(value)
}

const compare = (kind, a, b) => {
  if (kind === SORT_KIND.TEXT) {
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
  }
  return a - b
}

// Keeps paging deterministic: rows that tie on the sorted column always come out
// in the same order, so page 2 does not reshuffle between renders.
const tieBreak = (a, b) => {
  const nameA = toText(a?.studentName) ?? ''
  const nameB = toText(b?.studentName) ?? ''
  if (nameA !== nameB) {
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
  }
  return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
}

// The proficiency ladder as a sortable rank. Unknown levels land just past the
// ladder so they group together at the end instead of being treated as blank.
const proficiencyRank = level => {
  if (isBlank(level)) {
    return null
  }
  const index = PROFICIENCY_ORDER.indexOf(String(level).trim().toLowerCase())
  return index === -1 ? PROFICIENCY_ORDER.length : index
}

// Single source of truth for the PROCTORING STATUS cell: the renderer prints this
// label and the sorter ranks it. Returns null when there is nothing to show.
const getProctoringLabel = record => {
  if (!record || record.scoresCalculated === false) {
    return null
  }
  if (record.proctoringCalculated === false || record.flagged === null || record.flagged === undefined) {
    return 'Evaluating'
  }
  return record.flagged === true ? 'Bad' : 'Good'
}

const proctoringRank = record => {
  const index = PROCTORING_ORDER.indexOf(getProctoringLabel(record))
  return index === -1 ? null : index
}

// rows: the FULL candidate list for the active tab (not the current page).
// column: the antd column definition that was clicked — needs `sortValue` and,
//   optionally, `sortKind` (defaults to text).
// order: antd's 'ascend' | 'descend' | null/undefined (null = restore API order).
function sortRows(rows, column, order) {
  const list = Array.isArray(rows) ? rows : []
  const canSort =
    column &&
    typeof column.sortValue === 'function' &&
    (order === 'ascend' || order === 'descend')

  if (!canSort) {
    return list
  }

  const kind = column.sortKind || SORT_KIND.TEXT
  const direction = order === 'ascend' ? 1 : -1

  return [...list].sort((rowA, rowB) => {
    const valueA = toComparable(kind, column.sortValue(rowA))
    const valueB = toComparable(kind, column.sortValue(rowB))

    // Blanks last regardless of direction — "no score yet" is not a low score.
    if (valueA === null && valueB === null) {
      return tieBreak(rowA, rowB)
    }
    if (valueA === null) {
      return 1
    }
    if (valueB === null) {
      return -1
    }

    const diff = compare(kind, valueA, valueB)
    return diff === 0 ? tieBreak(rowA, rowB) : diff * direction
  })
}

module.exports = {
  SORT_KIND,
  PROFICIENCY_ORDER,
  PROCTORING_ORDER,
  getProctoringLabel,
  proficiencyRank,
  proctoringRank,
  sortRows,
}
